<template>
  <div class="public-shop learn-portal" :style="{ '--theme-color': themeColor }">
    <header class="shop-header" :style="{ background: themeGradientStyle }">
      <div class="container py-3">
        <div class="d-flex justify-content-between align-items-start flex-wrap">
          <div class="header-text">
            <small v-if="shopName" class="d-block opacity-90 mb-1">
              <i class="fas fa-store"></i> {{ shopName }}
            </small>
            <h2 class="mb-0 h5 font-weight-bold">{{ course && course.title }}</h2>
          </div>
          <div v-if="enrollment" class="progress-badge text-right mt-2 mt-md-0">
            <span class="badge badge-light px-3 py-2">
              <i class="fas fa-chart-line"></i> {{ enrollment.progress_pct || 0 }}% selesai
            </span>
          </div>
        </div>
        <div v-if="enrollment" class="header-progress mt-3">
          <div class="d-flex justify-content-between small mb-1">
            <span>Progress kursus</span>
            <span>{{ completedCount }}/{{ totalLessons }} pelajaran</span>
          </div>
          <div class="progress learn-progress-bar">
            <div
              class="progress-bar"
              role="progressbar"
              :style="{ width: (enrollment.progress_pct || 0) + '%' }"
              :aria-valuenow="enrollment.progress_pct || 0"
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>
        </div>
      </div>
    </header>

    <div class="container mt-4 pb-5">
      <!-- Loading -->
      <div v-if="loading" class="card p-5 text-center shop-card">
        <div class="spinner-border text-primary"></div>
        <p class="text-muted mt-3 mb-0">Memuat portal belajar...</p>
      </div>

      <!-- Error / invalid access -->
      <div v-else-if="errorMessage" class="card p-5 text-center shop-card">
        <i class="fas fa-exclamation-circle text-danger status-icon"></i>
        <h4 class="mt-2">{{ errorMessage }}</h4>
        <p class="text-muted">Periksa link akses dari WhatsApp atau email Anda.</p>
      </div>

      <!-- Main portal -->
      <div v-else-if="portal && course">
        <!-- Mobile curriculum toggle -->
        <button
          class="btn btn-outline-primary btn-block d-md-none mb-3 curriculum-toggle"
          @click="sidebarOpen = !sidebarOpen"
        >
          <i class="fas" :class="sidebarOpen ? 'fa-times' : 'fa-list'"></i>
          {{ sidebarOpen ? 'Tutup Kurikulum' : 'Daftar Pelajaran' }}
          <span class="badge badge-primary ml-2">{{ completedCount }}/{{ totalLessons }}</span>
        </button>

        <div class="row">
          <!-- Sidebar -->
          <div
            class="col-md-4 mb-3 mb-md-0"
            :class="{ 'd-none d-md-block': !sidebarOpen, 'd-block': sidebarOpen }"
          >
            <div class="card shop-card curriculum-card p-3">
              <h6 class="text-muted text-uppercase small mb-3">
                <i class="fas fa-book-open"></i> Kurikulum
              </h6>
              <div v-for="mod in course.modules" :key="mod.id" class="module-block mb-3">
                <div class="module-title">{{ mod.title }}</div>
                <div
                  v-for="lesson in mod.lessons"
                  :key="lesson.id"
                  class="lesson-item"
                  :class="{
                    active: activeLesson && activeLesson.id === lesson.id,
                    locked: !lesson.can_access,
                    completed: lesson.completed,
                  }"
                  @click="selectLesson(lesson)"
                >
                  <span class="lesson-icon">
                    <i v-if="lesson.completed" class="fas fa-check-circle text-success"></i>
                    <i v-else-if="!lesson.can_access" class="fas fa-lock text-muted"></i>
                    <i v-else class="fas" :class="lessonIcon(lesson.content_type)"></i>
                  </span>
                  <span class="lesson-title">{{ lesson.title }}</span>
                  <small v-if="lesson.duration_minutes" class="lesson-duration text-muted">
                    {{ lesson.duration_minutes }}m
                  </small>
                </div>
              </div>
            </div>
          </div>

          <!-- Lesson content -->
          <div class="col-md-8">
            <div v-if="activeLesson" class="card shop-card lesson-card p-4">
              <div class="lesson-header mb-3">
                <span class="badge badge-light text-uppercase small">{{ contentTypeLabel(activeLesson.content_type) }}</span>
                <h4 class="mt-2 mb-0">{{ activeLesson.title }}</h4>
              </div>

              <div v-if="activeLesson.content_type === 'video' && activeLesson.video_url" class="mb-4">
                <div class="embed-responsive embed-responsive-16by9 video-embed">
                  <iframe
                    class="embed-responsive-item"
                    :src="embedUrl(activeLesson.video_url)"
                    allowfullscreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  ></iframe>
                </div>
              </div>

              <div v-else-if="activeLesson.content_type === 'pdf' && activeLesson.content" class="mb-4">
                <a :href="activeLesson.content" target="_blank" rel="noopener" class="btn btn-outline-primary btn-lg pdf-btn">
                  <i class="fas fa-file-pdf"></i> Buka / Download PDF
                </a>
              </div>

              <div v-else-if="activeLesson.content_type === 'quiz'" class="mb-4 alert alert-info">
                <i class="fas fa-question-circle"></i> Quiz — fitur quiz interaktif coming soon.
              </div>

              <div
                v-if="activeLesson.content && activeLesson.content_type !== 'pdf'"
                class="mb-4 lesson-content"
                v-html="activeLesson.content"
              ></div>

              <div class="lesson-actions">
                <button
                  v-if="activeLesson.can_access && !activeLesson.completed"
                  class="btn btn-success btn-lg btn-block complete-btn"
                  @click="markComplete"
                  :disabled="marking"
                >
                  <span v-if="marking"><i class="fas fa-spinner fa-spin"></i> Menyimpan...</span>
                  <span v-else><i class="fas fa-check"></i> Tandai Selesai</span>
                </button>
                <div v-else-if="activeLesson.completed" class="completed-banner text-center">
                  <i class="fas fa-check-circle text-success"></i>
                  <span class="text-success font-weight-bold ml-1">Pelajaran selesai</span>
                </div>
              </div>
            </div>

            <div v-else class="card shop-card p-5 text-center text-muted">
              <i class="fas fa-hand-pointer empty-icon"></i>
              <p class="mb-0 mt-2">Pilih pelajaran dari daftar kurikulum</p>
            </div>
          </div>
        </div>

        <!-- Mobile bottom bar -->
        <div v-if="activeLesson && activeLesson.can_access && !activeLesson.completed" class="mobile-bottom-bar d-md-none">
          <button class="btn btn-success btn-block" @click="markComplete" :disabled="marking">
            <i class="fas fa-check"></i> Tandai Selesai
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import publicShopPaths from './public_shop_paths';
import { applyShopTheme, shopHeaderBackground, pickShopTheme, shopPublicMixin } from '@/components/shop_public/shop_public_utils';
import '@/components/shop_public/shop_public.css';

export default {
  mixins: [publicShopPaths, shopPublicMixin],
  data() {
    return {
      loading: true,
      marking: false,
      errorMessage: '',
      sidebarOpen: false,
      portal: null,
      course: null,
      enrollment: null,
      shopName: '',
      activeLesson: null,
      themeColor: '#1565c0',
      token: '',
    };
  },
  computed: {
    themeGradientStyle() {
      return shopHeaderBackground(pickShopTheme(this.portal, { theme_color: this.themeColor }));
    },
    totalLessons() {
      if (!this.course?.modules) return 0;
      return this.course.modules.reduce((sum, mod) => sum + (mod.lessons?.length || 0), 0);
    },
    completedCount() {
      if (!this.course?.modules) return 0;
      let count = 0;
      for (const mod of this.course.modules) {
        for (const l of mod.lessons || []) {
          if (l.completed) count++;
        }
      }
      return count;
    },
  },
  mounted() {
    this.token = this.$route.query.token || '';
    if (!this.token) {
      this.errorMessage = 'Link akses tidak valid';
      this.loading = false;
      return;
    }
    this.load();
  },
  methods: {
    embedUrl(url) {
      if (!url) return '';
      if (url.includes('youtube.com/watch')) {
        const id = url.split('v=')[1]?.split('&')[0];
        return id ? 'https://www.youtube.com/embed/' + id : url;
      }
      if (url.includes('youtu.be/')) {
        const id = url.split('youtu.be/')[1]?.split('?')[0];
        return id ? 'https://www.youtube.com/embed/' + id : url;
      }
      return url;
    },
    lessonIcon(type) {
      return {
        video: 'fa-play-circle text-primary',
        text: 'fa-file-alt text-secondary',
        pdf: 'fa-file-pdf text-danger',
        quiz: 'fa-question-circle text-info',
      }[type] || 'fa-book text-muted';
    },
    contentTypeLabel(type) {
      return { video: 'Video', text: 'Materi', pdf: 'PDF', quiz: 'Quiz' }[type] || 'Pelajaran';
    },
    async load() {
      this.errorMessage = '';
      try {
        const r = await this.$store.dispatch('public_shop/public_get_learning_portal', this.token);
        this.portal = r.data.data;
        this.course = this.portal.course;
        this.enrollment = this.portal.enrollment;
        this.shopName = this.portal.shop_name || '';
        applyShopTheme(this.portal);
        this.themeColor = this.portal.theme_color || '#1565c0';
        this.activeLesson = null;
        for (const mod of this.course.modules) {
          for (const l of mod.lessons) {
            if (l.can_access) {
              this.activeLesson = l;
              break;
            }
          }
          if (this.activeLesson) break;
        }
      } catch (e) {
        this.errorMessage = e.response?.data?.message || 'Akses ditolak';
        this.portal = null;
      } finally {
        this.loading = false;
      }
    },
    selectLesson(lesson) {
      if (!lesson.can_access) {
        this.$toast.info('Pelajaran ini terkunci. Selesaikan pembayaran terlebih dahulu.');
        return;
      }
      this.activeLesson = lesson;
      if (window.innerWidth < 768) {
        this.sidebarOpen = false;
      }
    },
    async markComplete() {
      if (!this.activeLesson) return;
      this.marking = true;
      try {
        const r = await this.$store.dispatch('public_shop/public_mark_lesson_complete', {
          token: this.token,
          lessonId: this.activeLesson.id,
        });
        this.activeLesson.completed = true;
        this.enrollment.progress_pct = r.data.data.progress_pct;
        this.$toast.success('Pelajaran ditandai selesai!');
        if (this.enrollment.progress_pct >= 100) {
          this.$toast.success('🎉 Selamat! Anda menyelesaikan kursus ini!');
        }
      } catch (e) {
        this.$toast.error('Gagal menandai selesai');
      } finally {
        this.marking = false;
      }
    },
  },
};
</script>

<style scoped>
.learn-portal {
  padding-bottom: 80px;
}
.header-text {
  color: #fff;
}
.header-progress .learn-progress-bar {
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.25);
}
.header-progress .progress-bar {
  background: #fff;
  border-radius: 999px;
}
.curriculum-card {
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}
.module-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  margin-bottom: 8px;
}
.lesson-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  margin-bottom: 4px;
}
.lesson-item:hover:not(.locked) {
  background: #f0f7ff;
}
.lesson-item.active {
  background: var(--shop-primary, #1976d2);
  color: #fff;
}
.lesson-item.active .lesson-duration,
.lesson-item.active .text-muted {
  color: rgba(255, 255, 255, 0.85) !important;
}
.lesson-item.locked {
  opacity: 0.55;
  cursor: not-allowed;
}
.lesson-icon {
  width: 20px;
  flex-shrink: 0;
  text-align: center;
  margin-top: 2px;
}
.lesson-title {
  flex: 1;
  font-size: 14px;
  line-height: 1.4;
}
.lesson-duration {
  font-size: 11px;
  white-space: nowrap;
}
.video-embed {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}
.lesson-content {
  line-height: 1.8;
  font-size: 15px;
}
.lesson-content >>> img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}
.lesson-content >>> h1,
.lesson-content >>> h2,
.lesson-content >>> h3 {
  margin-top: 1.2em;
  margin-bottom: 0.6em;
}
.pdf-btn {
  border-radius: 10px;
}
.complete-btn {
  border-radius: 10px;
  font-weight: 600;
}
.completed-banner {
  background: #e8f5e9;
  border-radius: 10px;
  padding: 16px;
}
.empty-icon {
  font-size: 48px;
  color: #bdbdbd;
}
.mobile-bottom-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 12px 16px;
  background: #fff;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.1);
  z-index: 100;
}
@media (min-width: 768px) {
  .learn-portal {
    padding-bottom: 48px;
  }
}
</style>
